import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, X, Tag } from 'lucide-react'
import Button from './Button'

/**
 * HabitManager Component
 * Allows adding habits one by one and tagging them for care planning
 */
const HabitManager = ({ 
  label, 
  habits = [], 
  onChange, 
  placeholder = 'Enter a habit...',
  required = false 
}) => {
  const [newHabit, setNewHabit] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)

  const handleAddHabit = () => {
    if (!newHabit.trim()) return
    
    const habit = {
      id: Date.now().toString(),
      text: newHabit.trim(),
      tagged: false,
    }
    
    onChange([...habits, habit])
    setNewHabit('')
  }

  const handleRemoveHabit = (index) => {
    const updated = habits.filter((_, i) => i !== index)
    onChange(updated)
  }

  const handleToggleTag = (index) => {
    const updated = habits.map((habit, i) => 
      i === index ? { ...habit, tagged: !habit.tagged } : habit
    )
    onChange(updated)
  }

  const handleEditHabit = (index, newText) => {
    const updated = habits.map((habit, i) => 
      i === index ? { ...habit, text: newText } : habit
    )
    onChange(updated)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAddHabit()
    }
  }

  const taggedCount = habits.filter(h => h.tagged).length

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {taggedCount > 0 && (
          <span className="text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
            <Tag size={14} />
            {taggedCount} tagged for care planning
          </span>
        )}
      </div>

      {/* Add new habit */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newHabit}
          onChange={(e) => setNewHabit(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <Button
          type="button"
          onClick={handleAddHabit}
          disabled={!newHabit.trim()}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Add
        </Button>
      </div>

      {/* Habits list */}
      <div className="space-y-2">
        <AnimatePresence>
          {habits.map((habit, index) => (
            <motion.div
              key={habit.id || index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                habit.tagged
                  ? 'border-primary-300 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              {editingIndex === index ? (
                <input
                  type="text"
                  value={habit.text}
                  onChange={(e) => handleEditHabit(index, e.target.value)}
                  onBlur={() => setEditingIndex(null)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setEditingIndex(null)
                    }
                  }}
                  className="flex-1 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              ) : (
                <span
                  className="flex-1 text-sm text-gray-900 dark:text-white cursor-text"
                  onDoubleClick={() => setEditingIndex(index)}
                >
                  {habit.text}
                </span>
              )}
              
              <button
                type="button"
                onClick={() => handleToggleTag(index)}
                className={`p-1.5 rounded-lg transition-colors ${
                  habit.tagged
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title={habit.tagged ? 'Remove tag' : 'Tag for care planning'}
              >
                <Tag size={16} style={{ opacity: habit.tagged ? 1 : 0.4 }} />
              </button>
              
              <button
                type="button"
                onClick={() => handleRemoveHabit(index)}
                className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                title="Remove habit"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {habits.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-4">
          No habits added yet. Add habits above to track lifestyle patterns.
        </p>
      )}
    </div>
  )
}

export default HabitManager

